/**
 * LiveMetricCard — MetricValue-aware card
 *
 * Renders based on MetricValue.status:
 *  live          → value + trend arrow + DataSourceBadge
 *  partial       → value + amber badge
 *  not_connected → DataSourceNotConnected (replaces card body)
 *
 * Trend color is driven by directionality:
 *  higher_is_better → green for ↑, red for ↓
 *  lower_is_better  → green for ↓, red for ↑
 *  neutral          → slate (no color)
 *
 * "About this metric" expandable panel shows:
 *  description, source, directionality, sample size, freshness
 */
import React, { useState, useRef, useEffect } from 'react';
import { FiArrowUp, FiArrowDown, FiMinus, FiInfo, FiChevronDown } from 'react-icons/fi';
import type { MetricValue, RequiredIntegration, MetricCatalogEntry } from '../../../types/insightsTypes';
import { NOT_CONNECTED_REGISTRY, METRIC_CATALOG } from '../../../types/insightsTypes';
import DataSourceBadge from './DataSourceBadge';
import DataSourceNotConnected from './DataSourceNotConnected';

interface LiveMetricCardProps {
  title: string;
  metric: MetricValue;
  icon: React.ReactNode;
  format?: 'number' | 'percent' | 'score' | 'currency' | 'ratio';
  target?: string;
  /** Integration key for NOT_CONNECTED tooltip */
  requiredIntegration?: RequiredIntegration;
  /** Metric catalog key for "About this metric" panel (e.g., 'overview.total_members') */
  metricKey?: string;
}

function formatValue(value: number | null, format: string): string {
  if (value == null) return '—';
  switch (format) {
    case 'percent':
      return `${Math.round(value * 100) / 100}%`;
    case 'score':
      return `${Math.round(value * 10) / 10}`;
    case 'currency':
      return `$${value.toLocaleString()}`;
    case 'ratio':
      return `${Math.round(value * 100) / 100}`;
    default:
      return value.toLocaleString();
  }
}

function getTrendColor(trend: number | null, directionality: string): string {
  if (trend == null || trend === 0 || directionality === 'neutral') return 'text-slate-500';
  const isPositive = directionality === 'higher_is_better' ? trend > 0 : trend < 0;
  return isPositive ? 'text-green-600' : 'text-red-600';
}

/** Directionality label for display */
function directionalityLabel(d: string): string {
  switch (d) {
    case 'higher_is_better': return '↑ Higher is better';
    case 'lower_is_better': return '↓ Lower is better';
    default: return '— Neutral';
  }
}

/** "About this metric" expandable panel */
const MetricLineagePanel: React.FC<{
  metric: MetricValue;
  catalogEntry?: MetricCatalogEntry;
}> = ({ metric, catalogEntry }) => {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [expanded]);

  return (
    <div className="mt-2 border-t border-slate-100">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors pt-2 w-full"
        aria-expanded={expanded}
        aria-label="About this metric"
      >
        <FiInfo size={11} />
        <span>About this metric</span>
        <FiChevronDown
          size={11}
          className={`ml-auto transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        style={{ maxHeight: expanded ? contentHeight : 0 }}
        className="overflow-hidden transition-all duration-200 ease-in-out"
      >
        <div ref={contentRef} className="pt-2 pb-1 space-y-1.5">
          {catalogEntry?.display_name && (
            <div className="text-xs">
              <span className="text-slate-400 font-medium">Metric: </span>
              <span className="text-slate-600">{catalogEntry.display_name}</span>
            </div>
          )}
          {catalogEntry?.source_view && (
            <div className="text-xs">
              <span className="text-slate-400 font-medium">Source: </span>
              <span className="text-slate-600 font-mono">{catalogEntry.source_view}</span>
            </div>
          )}
          <div className="text-xs">
            <span className="text-slate-400 font-medium">Direction: </span>
            <span className="text-slate-600">{directionalityLabel(metric.directionality)}</span>
          </div>
          <div className="text-xs">
            <span className="text-slate-400 font-medium">Sample Size: </span>
            <span className="text-slate-600">{metric.sample_size.toLocaleString()} members</span>
          </div>
          <div className="text-xs">
            <span className="text-slate-400 font-medium">Sufficiency: </span>
            <span className={`font-medium ${
              metric.sufficiency === 'standard' ? 'text-green-600' :
              metric.sufficiency === 'directional' ? 'text-amber-600' :
              'text-red-500'
            }`}>{metric.sufficiency}</span>
          </div>
          {catalogEntry?.section && (
            <div className="text-xs">
              <span className="text-slate-400 font-medium">Category: </span>
              <span className="text-slate-600">{catalogEntry.section.replace('_', ' ')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LiveMetricCard: React.FC<LiveMetricCardProps> = ({
  title,
  metric,
  icon,
  format = 'number',
  target,
  requiredIntegration,
  metricKey,
}) => {
  // Lookup catalog entry for "About this metric"
  const catalogEntry = metricKey
    ? METRIC_CATALOG.find((e) => e.metric_key === metricKey)
    : undefined;

  // NOT_CONNECTED: show integration requirement instead of fake value
  if (metric.status === 'not_connected') {
    const integration = requiredIntegration ?? (metric.source.includes('LOS') ? 'los' : 'algorithm');
    const info = NOT_CONNECTED_REGISTRY[integration] ?? NOT_CONNECTED_REGISTRY.algorithm;
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
          <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">{icon}</div>
        </div>
        <DataSourceNotConnected info={info} inline />
      </div>
    );
  }

  // LIVE or PARTIAL: show real value
  const trendColor = getTrendColor(metric.trend, metric.directionality);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
            <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">{icon}</div>
          </div>

          <div className="mb-3">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">
              {formatValue(metric.value, format)}
            </p>
            <div className="flex items-center gap-2">
              {metric.trend != null && (
                <span className={`flex items-center text-xs sm:text-sm font-medium ${trendColor}`}>
                  {metric.trend > 0 ? (
                    <FiArrowUp className="mr-0.5" size={12} />
                  ) : metric.trend < 0 ? (
                    <FiArrowDown className="mr-0.5" size={12} />
                  ) : (
                    <FiMinus className="mr-0.5" size={12} />
                  )}
                  {metric.trend > 0 ? '+' : ''}
                  {Math.round(metric.trend * 100) / 100}
                </span>
              )}
              <DataSourceBadge status={metric.status} source={metric.source} compact />
            </div>
          </div>

          {target && (
            <div className="pt-3 border-t border-slate-200">
              <p className="text-slate-500 text-xs sm:text-sm">
                Target: <span className="font-medium text-slate-700">{target}</span>
              </p>
            </div>
          )}

          {/* About this metric — expandable lineage panel */}
          <MetricLineagePanel metric={metric} catalogEntry={catalogEntry} />
        </div>
      </div>
    </div>
  );
};

export default LiveMetricCard;
